// Port operations run on Barcelona time, so times are shown and edited in
// Europe/Madrid whatever the viewer's own time zone. Stored as ISO-8601.
const TIME_ZONE = 'Europe/Madrid'

const parts = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

interface LocalParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const toLocalParts = (ms: number): LocalParts => {
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.formatToParts(ms).find(part => part.type === type)?.value)
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') }
}

const pad = (n: number): string => n.toString().padStart(2, '0')

/** "DD/MM/YYYY HH:mm" in Barcelona time, the format bombeo-app shows; '' for an unparseable value. */
export const formatDateTime = (iso: string): string => {
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  const p = toLocalParts(ms)
  return `${pad(p.day)}/${pad(p.month)}/${String(p.year)} ${pad(p.hour)}:${pad(p.minute)}`
}

/** The year it is in Barcelona at `ms`. */
export const barcelonaYear = (ms: number): number => toLocalParts(ms).year

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/

/**
 * ISO → the "YYYY-MM-DDTHH:mm" Barcelona-time value a datetime-local input shows.
 * A value that already is one is kept: React Admin's DateTimeInput applies its
 * `format` to a value `useInput` has already formatted, and Date.parse would
 * read that offset-less value in the viewer's zone, not Barcelona's.
 */
export const toDateTimeInput = (iso: unknown): string => {
  if (typeof iso !== 'string' || iso === '') return ''
  if (LOCAL_INPUT.test(iso)) return iso.slice(0, 16)
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return ''
  const p = toLocalParts(ms)
  return `${String(p.year)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

/** A "YYYY-MM-DDTHH:mm[:ss]" Barcelona-time value → ISO; null when empty or invalid. */
export const fromDateTimeInput = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const match = LOCAL_INPUT.exec(value)
  if (!match) return null
  const [year, month, day, hour, minute, second] = (match.slice(1) as (string | undefined)[]).map(n =>
    n === undefined ? 0 : Number(n),
  ) as [number, number, number, number, number, number]
  const wanted = Date.UTC(year, month - 1, day, hour, minute, second)
  // Start from the wall time read as UTC and correct by Barcelona's offset at
  // that instant; a second pass settles the guesses that straddle a DST change.
  let ms = wanted
  for (let i = 0; i < 2; i++) {
    const p = toLocalParts(ms)
    ms += wanted - Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  }
  const p = toLocalParts(ms)
  const roundTrips = p.year === year && p.month === month && p.day === day && p.hour === hour && p.minute === minute
  return roundTrips ? new Date(ms).toISOString() : null
}

const HOUR = 3_600_000

/** Every Barcelona midnight in [from, to], ascending. */
export const barcelonaMidnights = (from: number, to: number): number[] => {
  const midnights: number[] = []
  // Barcelona is a whole number of hours off UTC, so its midnights fall on UTC hours.
  for (let at = Math.ceil(from / HOUR) * HOUR; at <= to; at += HOUR) {
    const p = toLocalParts(at)
    if (p.hour === 0 && p.minute === 0) midnights.push(at)
  }
  return midnights
}

/** "DD/MM" in Barcelona time. */
export const formatDay = (ms: number): string => {
  const p = toLocalParts(ms)
  return `${pad(p.day)}/${pad(p.month)}`
}
